import type { Span as OtelSpan, Tracer } from "@opentelemetry/api";
import { context, SpanStatusCode, trace } from "@opentelemetry/api";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { BasicTracerProvider, BatchSpanProcessor } from "@opentelemetry/sdk-trace-node";
import type {
  Attributes,
  GenerationEvent,
  Span,
  SpanOptions,
  SpanResult,
  TelemetryConfig,
  TelemetryProvider,
  Trace,
  TraceOptions,
} from "../types.js";
import { createOtlpExporter } from "./exporter.js";
import { generationAttributes } from "./gen-ai.js";

/**
 * OpenTelemetry-backed telemetry provider. Exports traces via OTLP/HTTP
 * to any compatible backend (Langfuse, Grafana Tempo, Jaeger, etc.).
 *
 * Always instantiate via the async static factory {@link OtelTelemetryProvider.create}
 * so the OTLP exporter is wired before any spans are created. Active OTEL spans
 * are tracked in an internal map keyed by `spanId`; both `flush()` and `shutdown()`
 * swallow export errors so telemetry failures never crash the agent.
 *
 * @docLink packages/telemetry/concepts#otel-telemetry-provider
 */
export class OtelTelemetryProvider implements TelemetryProvider {
  private readonly tracer: Tracer;
  private readonly tracerProvider: BasicTracerProvider;

  private readonly spanMap = new Map<string, OtelSpan>();

  /**
   * Use OtelTelemetryProvider.create(config) instead of new.
   * The static factory awaits exporter setup so no early spans are lost.
   */
  private constructor(_config: TelemetryConfig, tracerProvider: BasicTracerProvider) {
    this.tracerProvider = tracerProvider;
    this.tracer = tracerProvider.getTracer("skaile-telemetry");
  }

  /** Async factory — ensures the exporter is wired before any spans are created. */
  static async create(config: TelemetryConfig): Promise<OtelTelemetryProvider> {
    const resource = resourceFromAttributes({
      "service.name": "skaile-workspaces",
      "service.version": "0.1.0",
    });

    const spanProcessors = [];

    if (config.endpoint) {
      const exporter = await createOtlpExporter(config);
      const processor = new BatchSpanProcessor(exporter, {
        maxExportBatchSize: config.batchSize,
        scheduledDelayMillis: config.exportIntervalMs,
      });
      spanProcessors.push(processor);
    }

    const tracerProvider = new BasicTracerProvider({ resource, spanProcessors });

    return new OtelTelemetryProvider(config, tracerProvider);
  }

  startTrace(opts: TraceOptions): Trace {
    const rootSpan = this.tracer.startSpan(opts.name, {
      attributes: {
        "skaile.trace.kind": opts.kind,
        ...(opts.attributes ?? {}),
      },
    });

    const spanContext = rootSpan.spanContext();
    const traceId = spanContext.traceId;
    const spanId = spanContext.spanId;

    this.spanMap.set(spanId, rootSpan);

    return {
      traceId,
      rootSpan: { spanId, traceId },
    };
  }

  endTrace(traceObj: Trace, result?: SpanResult): void {
    this.endSpan(traceObj.rootSpan, result);
  }

  startSpan(parent: Trace | Span, opts: SpanOptions): Span {
    const parentSpanId = "rootSpan" in parent ? parent.rootSpan.spanId : parent.spanId;
    const parentOtelSpan = this.spanMap.get(parentSpanId);
    let childSpan: OtelSpan;

    if (parentOtelSpan) {
      const parentCtx = trace.setSpan(context.active(), parentOtelSpan);
      childSpan = this.tracer.startSpan(
        opts.name,
        {
          attributes: {
            "skaile.span.kind": opts.kind,
            ...(opts.attributes ?? {}),
          },
        },
        parentCtx,
      );
    } else {
      childSpan = this.tracer.startSpan(opts.name, {
        attributes: {
          "skaile.span.kind": opts.kind,
          ...(opts.attributes ?? {}),
        },
      });
    }

    const spanContext = childSpan.spanContext();
    this.spanMap.set(spanContext.spanId, childSpan);

    return {
      spanId: spanContext.spanId,
      traceId: spanContext.traceId,
    };
  }

  endSpan(span: Span, result?: SpanResult): void {
    const otelSpan = this.spanMap.get(span.spanId);
    if (!otelSpan) return;

    if (result?.attributes) {
      otelSpan.setAttributes(result.attributes);
    }

    if (result?.status === "error") {
      otelSpan.setStatus({
        code: SpanStatusCode.ERROR,
        message: result.error,
      });
    } else {
      otelSpan.setStatus({ code: SpanStatusCode.OK });
    }

    otelSpan.end();
    this.spanMap.delete(span.spanId);
  }

  addEvent(span: Span, name: string, attrs?: Attributes): void {
    const otelSpan = this.spanMap.get(span.spanId);
    otelSpan?.addEvent(name, attrs);
  }

  logGeneration(span: Span, generation: GenerationEvent): void {
    const otelSpan = this.spanMap.get(span.spanId);
    if (!otelSpan) return;

    const attrs = generationAttributes(generation);
    otelSpan.setAttributes(attrs);
    otelSpan.addEvent("gen_ai.generation", attrs);
  }

  recordMetric(_name: string, _value: number, _tags?: Record<string, string>): void {
    // Phase 2: wire to OTEL Metrics API when we add a metrics backend
  }

  async flush(): Promise<void> {
    try {
      await withTimeout(this.tracerProvider.forceFlush(), TELEMETRY_DRAIN_TIMEOUT_MS);
    } catch {
      // Swallow export errors (e.g. no collector running) — telemetry
      // failures must never crash the agent.
    }
  }

  async shutdown(): Promise<void> {
    try {
      await withTimeout(this.tracerProvider.shutdown(), TELEMETRY_DRAIN_TIMEOUT_MS);
    } catch {
      // Swallow export errors on shutdown.
    }
    this.spanMap.clear();
  }
}

/**
 * Upper bound on how long `flush()` / `shutdown()` may block on the OTLP
 * exporter. Telemetry is a side-channel — when the collector is unreachable
 * the exporter can hang well past any test or shutdown deadline, so the drain
 * is abandoned rather than allowed to block the caller indefinitely.
 */
const TELEMETRY_DRAIN_TIMEOUT_MS = 2_000;

/** Resolve when `promise` settles or `ms` elapses, whichever comes first. */
function withTimeout(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    promise.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}
